import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Pencil, Trash2, Power, KeyRound, Copy, Check, X, Loader2, ShieldCheck } from 'lucide-react';
import { useAdmin } from '../context/AdminContext';
import AdminNav from './AdminNav';

// Readable random password (no 0/O/1/l) for the owner to share with a new team member.
function generatePassword(length = 14) {
  const chars = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = new Uint32Array(length);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => chars[b % chars.length]).join('');
}

const formatDate = (d) => (d ? new Date(d).toLocaleString() : 'Never');

const inputClass =
  'w-full bg-slate-950 border border-white/10 rounded-xl px-3 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/20 transition-all';

function PermissionPicker({ permissions, role, value, onChange }) {
  if (role === 'owner') {
    return <p className="text-[11px] text-slate-400">Owners can open every page and manage the team.</p>;
  }
  const toggle = (key) => {
    let next = value.includes(key) ? value.filter((k) => k !== key) : [...value, key];
    if (key === 'users_edit' && next.includes('users_edit') && !next.includes('users')) next = [...next, 'users'];
    if (key === 'users' && !next.includes('users')) next = next.filter((k) => k !== 'users_edit');
    onChange(next);
  };
  return (
    <div className="space-y-2">
      {permissions.map((p) => (
        <label key={p.key} className="flex items-start gap-2.5 cursor-pointer group">
          <input
            type="checkbox"
            checked={value.includes(p.key)}
            onChange={() => toggle(p.key)}
            className="mt-0.5 accent-indigo-500"
          />
          <span>
            <span className="text-xs font-semibold text-slate-200 group-hover:text-white">{p.label}</span>
            <span className="block text-[10px] text-slate-500">{p.description}</span>
          </span>
        </label>
      ))}
    </div>
  );
}

function PasswordField({ value, onChange, placeholder }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked: the password is visible in the field anyway */
    }
  };
  return (
    <div className="flex gap-2">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`${inputClass} font-mono`}
        autoComplete="new-password"
      />
      <button type="button" onClick={() => onChange(generatePassword())} title="Generate a password"
        className="px-3 rounded-xl bg-slate-900/60 border border-white/10 text-slate-300 hover:text-white hover:border-indigo-500/30">
        <KeyRound className="w-4 h-4" />
      </button>
      <button type="button" onClick={copy} disabled={!value} title="Copy password"
        className="px-3 rounded-xl bg-slate-900/60 border border-white/10 text-slate-300 hover:text-white hover:border-indigo-500/30 disabled:opacity-40">
        {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
}

function AdminForm({ permissions, initial, isSelf, onCancel, onSubmit, submitting, error }) {
  const isNew = !initial;
  const [name, setName] = useState(initial?.name || '');
  const [email, setEmail] = useState(initial?.email || '');
  const [password, setPassword] = useState(isNew ? generatePassword() : '');
  const [role, setRole] = useState(initial?.role || 'member');
  const [perms, setPerms] = useState(initial?.role === 'member' ? initial.permissions : ['growth']);

  const submit = (e) => {
    e.preventDefault();
    const body = { name, role, permissions: role === 'owner' ? [] : perms };
    if (isNew) body.email = email;
    if (password) body.password = password;
    onSubmit(body);
  };

  return (
    <form onSubmit={submit} className="glass-panel rounded-2xl p-5 border border-indigo-500/20 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-black text-white">{isNew ? 'Add a team member' : `Edit ${initial.name}`}</h3>
        <button type="button" onClick={onCancel} className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <label className="space-y-1.5 block">
          <span className="text-[11px] font-semibold text-slate-400">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required className={inputClass} placeholder="Jane Doe" />
        </label>
        <label className="space-y-1.5 block">
          <span className="text-[11px] font-semibold text-slate-400">Email (used to sign in)</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={!isNew}
            className={`${inputClass} disabled:opacity-50`} placeholder="jane@company.com" />
        </label>
        <label className="space-y-1.5 block md:col-span-2">
          <span className="text-[11px] font-semibold text-slate-400">
            {isNew ? 'Password (min. 8 characters, share it privately)' : 'New password (leave empty to keep the current one)'}
          </span>
          <PasswordField value={password} onChange={setPassword} placeholder={isNew ? '' : 'Unchanged'} />
        </label>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <label className="space-y-1.5 block">
          <span className="text-[11px] font-semibold text-slate-400">Role</span>
          <select value={role} onChange={(e) => setRole(e.target.value)} disabled={isSelf} className={`${inputClass} disabled:opacity-50`}>
            <option value="member">Team member (only the pages ticked below)</option>
            <option value="owner">Owner (everything, including this Team page)</option>
          </select>
          {isSelf && <span className="text-[10px] text-slate-500">You can't change your own role.</span>}
        </label>
        <div className="space-y-1.5">
          <span className="text-[11px] font-semibold text-slate-400 block">Access</span>
          <PermissionPicker permissions={permissions} role={role} value={perms} onChange={setPerms} />
        </div>
      </div>

      {error && <p className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/20 rounded-xl px-3 py-2">{error}</p>}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-300 hover:text-white">Cancel</button>
        <button type="submit" disabled={submitting || (role === 'member' && perms.length === 0)}
          className="px-4 py-2 rounded-xl text-xs font-bold bg-indigo-500 hover:bg-indigo-400 text-white disabled:opacity-50 inline-flex items-center gap-2">
          {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {isNew ? 'Create account' : 'Save changes'}
        </button>
      </div>
      {role === 'member' && perms.length === 0 && (
        <p className="text-[10px] text-amber-300 text-right">Tick at least one page.</p>
      )}
    </form>
  );
}

export default function TeamPage() {
  const { API_BASE, apiFetch, admin: me } = useAdmin();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null); // null | 'new' | admin object
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [rowError, setRowError] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-team'],
    queryFn: async () => {
      const res = await apiFetch(`${API_BASE}/team`);
      if (!res) throw new Error('Session expired');
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to load the team');
      return res.json();
    },
  });

  const call = async (method, path, body) => {
    const res = await apiFetch(`${API_BASE}/team${path}`, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res) return { ok: false, error: 'Session expired.' };
    if (res.status === 204) return { ok: true };
    const json = await res.json().catch(() => ({}));
    return res.ok ? { ok: true, data: json } : { ok: false, error: json.error || 'Something went wrong.' };
  };

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin-team'] });

  const save = async (body) => {
    setSubmitting(true);
    setFormError('');
    const result = editing === 'new' ? await call('POST', '', body) : await call('PATCH', `/${editing.id}`, body);
    setSubmitting(false);
    if (!result.ok) return setFormError(result.error);
    setEditing(null);
    refresh();
  };

  const toggleActive = async (a) => {
    setRowError('');
    const result = await call('PATCH', `/${a.id}`, { active: !a.active });
    if (!result.ok) setRowError(result.error);
    refresh();
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete ${a.name} (${a.email})? They will be signed out and can't log in again.`)) return;
    setRowError('');
    const result = await call('DELETE', `/${a.id}`);
    if (!result.ok) setRowError(result.error);
    refresh();
  };

  const permissions = data?.permissions || [];
  const labelOf = (key) => permissions.find((p) => p.key === key)?.label || key;

  return (
    <div className="min-h-screen bg-[#070a13] text-slate-200 flex flex-col font-sans">
      <AdminNav />
      <main className="flex-1 max-w-7xl w-full mx-auto p-4 sm:p-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-white tracking-tight flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-indigo-400" /> Team
            </h1>
            <p className="text-xs text-slate-400 mt-1">
              Create admin panel accounts for your team and choose which pages each person can open.
            </p>
          </div>
          {editing === null && (
            <button onClick={() => { setFormError(''); setEditing('new'); }}
              className="px-4 py-2.5 rounded-xl text-xs font-bold bg-indigo-500 hover:bg-indigo-400 text-white inline-flex items-center gap-2 self-start">
              <UserPlus className="w-4 h-4" /> Add team member
            </button>
          )}
        </div>

        {editing !== null && (
          <AdminForm
            key={editing === 'new' ? 'new' : editing.id}
            permissions={permissions}
            initial={editing === 'new' ? null : editing}
            isSelf={editing !== 'new' && editing.id === me?.id}
            onCancel={() => setEditing(null)}
            onSubmit={save}
            submitting={submitting}
            error={formError}
          />
        )}

        {rowError && <p className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/20 rounded-xl px-3 py-2">{rowError}</p>}

        <div className="glass-panel rounded-2xl overflow-hidden border border-white/[0.05]">
          {isLoading ? (
            <div className="p-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-indigo-400" /></div>
          ) : error ? (
            <p className="p-6 text-xs text-rose-300">{error.message}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-left">
                <thead>
                  <tr className="border-b border-white/[0.06] bg-slate-900/40 text-slate-400 text-[10px] font-bold uppercase tracking-widest">
                    <th className="py-3.5 px-5">Person</th>
                    <th className="py-3.5 px-5">Role</th>
                    <th className="py-3.5 px-5">Access</th>
                    <th className="py-3.5 px-5">Last sign-in</th>
                    <th className="py-3.5 px-5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {(data?.admins || []).map((a) => {
                    const isSelf = a.id === me?.id;
                    return (
                      <tr key={a.id} className={a.active ? '' : 'opacity-50'}>
                        <td className="py-3.5 px-5">
                          <span className="text-xs font-bold text-white block">
                            {a.name} {isSelf && <span className="text-[10px] font-semibold text-indigo-300">(you)</span>}
                          </span>
                          <span className="text-[10px] text-slate-500 block">{a.email}</span>
                        </td>
                        <td className="py-3.5 px-5">
                          <span className={`text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded border ${a.role === 'owner'
                            ? 'border-amber-500/20 text-amber-300 bg-amber-500/5'
                            : 'border-indigo-500/20 text-indigo-300 bg-indigo-500/5'}`}>
                            {a.role === 'owner' ? 'Owner' : 'Member'}
                          </span>
                          {!a.active && <span className="ml-2 text-[10px] font-bold text-rose-300">Disabled</span>}
                        </td>
                        <td className="py-3.5 px-5">
                          <div className="flex flex-wrap gap-1">
                            {a.role === 'owner'
                              ? <span className="text-[10px] text-slate-400">Everything</span>
                              : a.permissions.map((k) => (
                                <span key={k} className="text-[10px] px-2 py-0.5 rounded bg-slate-800 text-slate-300">{labelOf(k)}</span>
                              ))}
                          </div>
                        </td>
                        <td className="py-3.5 px-5 text-[11px] text-slate-400">{formatDate(a.lastLoginAt)}</td>
                        <td className="py-3.5 px-5">
                          <div className="flex justify-end gap-1.5">
                            <button onClick={() => { setFormError(''); setEditing(a); }} title="Edit, change access or reset password"
                              className="p-2 rounded-lg border border-white/5 bg-slate-900/40 text-slate-300 hover:text-white hover:border-indigo-500/30">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            {!isSelf && (
                              <>
                                <button onClick={() => toggleActive(a)} title={a.active ? 'Disable (signs them out)' : 'Enable'}
                                  className="p-2 rounded-lg border border-white/5 bg-slate-900/40 text-slate-300 hover:text-amber-300 hover:border-amber-500/30">
                                  <Power className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => remove(a)} title="Delete"
                                  className="p-2 rounded-lg border border-white/5 bg-slate-900/40 text-slate-300 hover:text-rose-300 hover:border-rose-500/30">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
